// ─── Imports ────────────────────────────────────────────────
import React, { forwardRef, useRef, useState, useEffect } from "react";
import classNames from "classnames";

// ─── Styles ─────────────────────────────────────────────────
import textAreaStyles from "./textArea.module.scss";
import { ReactComponent as Up } from "../../../assets/images/svg/up.svg";
import { ReactComponent as Down } from "../../../assets/images/svg/down.svg";

// ─── Enums ──────────────────────────────────────────────────
export enum InputType {
  DEFAULT = "DEFAULT",
  NUMBER = "NUMBER",
}

// ─── Props Interface ────────────────────────────────────────
interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  defaultValue?: string | number;
  maxCharacters?: number;
  description?: string;
  required?: boolean;
  errorFormat?: boolean;
  errorMessage?: string;
  height?: string;
  textRows?: number;
  rangeSelector?: [number, number];
  type?: InputType;
}

// ─── Component ──────────────────────────────────────────────
const TextArea = forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      label = "",
      defaultValue = "",
      maxCharacters = 100,
      description,
      required,
      errorFormat = false,
      errorMessage,
      height,
      textRows = 1,
      rangeSelector,
      type = InputType.DEFAULT,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const [focused, setFocused] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const rows = Math.ceil(maxCharacters / 100);
    const errorClass = errorFormat || error ? "error" : "";

    useEffect(() => {
      setError(errorFormat);
    }, [errorFormat]);

    useEffect(() => {
      if (required && focused && !inputRef.current?.value) {
        setError(true);
      } else {
        setError(false);
      }
    }, [focused, required]);

    const validateNumber = (
      key: React.KeyboardEvent<HTMLTextAreaElement>["key"]
    ) => /[٠-٩0-9.]/.test(key);

    // ─── Increase / Decrease Buttons ─────────────────────────
    const updateRangeValue = (step: number) => {
      if (!inputRef.current) return;
      const currentVal = Number(inputRef.current.value);
      const newVal = currentVal + step;

      if (
        rangeSelector &&
        newVal >= rangeSelector[0] &&
        newVal <= rangeSelector[1]
      ) {
        inputRef.current.value = newVal.toString();
        const event = new Event("input", { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }
    };

    return (
      <div
        className={textAreaStyles["outerContainer"]}
        onClick={() => inputRef.current?.focus()}>
        <div
          style={{ height }}
          className={classNames(
            textAreaStyles["container"],
            textAreaStyles[`${errorClass}`]
          )}>
          {label && (
            <div className={textAreaStyles["header"]}>
              <p>{label}</p>
            </div>
          )}

          <textarea
            ref={(el) => {
              inputRef.current = el;
              if (typeof ref === "function") ref(el);
              else if (ref) ref.current = el;
            }}
            rows={textRows || rows}
            className={textAreaStyles["text_area"]}
            maxLength={maxCharacters}
            defaultValue={defaultValue}
            onFocus={() => setFocused(true)}
            onPaste={(event) => {
              if (type === InputType.NUMBER) event.preventDefault();
            }}
            onKeyPress={(event) => {
              if (
                (event.charCode === 13 || event.code === "Enter") &&
                rows <= 1
              )
                event.preventDefault();
              if (!validateNumber(event.key) && type === InputType.NUMBER)
                event.preventDefault();
            }}
            inputMode={type === InputType.NUMBER ? "decimal" : "text"}
            style={{
              minHeight: !height ? `${rows - 0.5}rem` : `${rows - 1}rem`,
            }}
            {...props}
          />

          {/* ─── Range Selector or Character Counter ─── */}
          {rangeSelector ? (
            <div className={textAreaStyles["range_selector"]}>
              <div onClick={() => updateRangeValue(+1)}>
                <Up />
              </div>
              <div onClick={() => updateRangeValue(-1)}>
                <Down />
              </div>
            </div>
          ) : (
            <div className={textAreaStyles["text_length"]}>
              {description && <p>{description}</p>}
            </div>
          )}
        </div>

        {errorMessage && (
          <div className={textAreaStyles["error_container"]}>
            <p dir={"rtl"} className={textAreaStyles["error_text"]}>
              {errorMessage}
            </p>
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";

export default React.memo(TextArea);
